import React, {useEffect} from "react";
import {
    Button,
    Card,
    CardContent,
    Chip,
    Grid,
    IconButton,
    LinearProgress,
    Link,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import { useTheme } from '@mui/material/styles';
import {Add, ArrowBack} from "@mui/icons-material";
import EnhancedSelect from "../../../../components/EnhancedSelect";
import EnhancedTextField from "../../../../components/EnhancedTextField";
import Helpers from "../../../../helpers";
import {List} from "react-content-loader";
import EnhancedAutocomplete from "../../../../components/EnhancedAutocomplete";
import EnhancedErrorDialog from "../../../../components/EnhancedErrorDialog";
import Config from "../../../../config";
import axios from "axios";
import {retailerCatalogSearch, supplierCatalogSearch} from "../catalog/catalog";
import useIsSmallScreen, {createProduct as utilCreateProduct, deleteProduct as utilDeleteProduct, getAllCategories as utilGetCategories, getAllTags as utilGetAllTags, getAttributes as utilGetAttributes, getAttributeSets as utilGetAttributeSets, getPosts, getPostThreads, getProduct as utilGetProduct, getThreadDetails, listingStatusColorMapper, scrollToGroup, updateProduct as utilUpdateProduct, utilCheckInventory, utilGetPartners, utilSearchInventory, utilSearchSupplierCatalog, isNullOrEmpty} from "../../../../utils/utils";
import moment from "moment";
import {cleanGroupAttributes} from "./components/AttributeGroup";
import {ProductDetail} from "./components/ProductDetail";
import ProductVariantManager from "./components/ProductVariantManager";
import cloneDeep from 'lodash/cloneDeep';
import EnhancedConfirmDialog from "../../../../components/EnhancedConfirmDialog";
import isEqual from "lodash.isequal";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ProductApproveDialog from "../../components/Dialogs/ProductApproveDialog";
import ProductRejectDialog from "../../components/Dialogs/ProductRejectDialog";
import UpdatingDialog from "../../../../components/Dialogs/UpdatingDialog";
import ErrorIcon from "@mui/icons-material/Error";
import Auth from "../../../../auth";
import {AttributeDiff} from "./components/AttributeDiff";
// TODO(ENG-344): remove unstable_batchedUpdates - no-op under React 18 auto-batching
import {unstable_batchedUpdates} from "react-dom";
import GenericDialog from "../../../../components/Dialogs/GenericDialog";
import {Timeline} from "react-event-timeline";
import {Post} from "../../../messages/thread";
import NewMessage from "../../components/Dialogs/NewMessage";
import {NavigationLink} from './components/NavigationLink'
import AttributesAndVariations from './components/AttributesAndVariations'
import LoadingComponent from './components/LoadingComponent'
import {RunAttributeRules} from "../../../inventory/shared/validation";
import { Alert } from '@mui/material';
import ProductSaveConfirm from "../../components/Dialogs/ProductSaveConfirm";
import ProductVariations from './components/ProductVariations'
import ProductVariantAdd from './components/ProductVariantAdd'
import EnhancedSnackbar from '../../../../components/EnhancedSnackbar'
import AIEnrichmentDialog from '../../components/Dialogs/AIEnrichmentDialog'
import AIEnrichmentDialogV2 from '../../components/Dialogs/AIEnrichmentDialogV2'
import ProductScoreCard from '../../components/Dialogs/ProductScoreCard'
import ProductScoreDialog from '../../components/Dialogs/ProductScoreDialog'
import { AI_ASSIST_FEATURES } from '../../../../utils/utils'
import { pageContentGap } from '../../../../theme';
import type {
    CatalogProduct,
    ProductDetailsState,
    ProductVariant,
} from "./types";

// region TypeScript Interfaces
interface CatalogManagerProductDetailsProps {
    setTitle?: (title: string, override?: boolean) => void;
    location: { search: string; pathname: string; key?: string };
    history: { push: (url: string) => void; replace: (url: string) => void; goBack: () => void };
    isRetailer?: boolean;
    retailers: Array<{ Id: string | number; Marketplace?: boolean; [key: string]: any }>;
    currentCatalog?: string;
}
// endregion

const iconButtonSx = (_theme: any) => ({
    backgroundColor: "white",
    border: "none",
    borderRadius: "6px",
    "& svg": { color: "rgba(0, 0, 0, 0.54)" }
});

const defaultProduct: CatalogProduct = {
    Id: 0,
    Attributes: {
        name: "",
        sku: "",
        description: "",
        attribute_set: "base",
        tags: [],
    },
    LastModified: new Date(),
}
function CatalogManagerProductDetails(props: CatalogManagerProductDetailsProps) {
    const {setTitle} = props;
    const theme = useTheme();
    const hasInventoryManagePermission = Auth.hasPermission("inventory/manage");
    const coid = parseInt(Auth.getAccountNumber());
    let urlProductId = new Helpers().queryString(props.location.search)["productId"];
    let urlPartnerId = new Helpers().queryString(props.location.search)["partnerId"];
    let urlCatalog = new Helpers().queryString(props.location.search)["catalog"];
    const formDisabled = !hasInventoryManagePermission || (!props.isRetailer && urlCatalog === "partnercatalog");
    const [productId, setProductId] = React.useState<ProductDetailsState["productId"]>(urlProductId !== undefined ? decodeURIComponent(urlProductId) : undefined);
    const [partnerId, setPartnerId] = React.useState<ProductDetailsState["partnerId"]>(urlPartnerId);
    const [catalog, setCatalog] = React.useState<ProductDetailsState["catalog"]>(urlCatalog);
    // eslint-disable-next-line
    const foundRetailer = props.retailers.find(retailer => retailer.Id == partnerId)?.Marketplace;
    const isPartnerMarketplace = props.isRetailer ? false : foundRetailer ?? false;
    const retailerCoid = props.isRetailer ? coid : (partnerId ? parseInt(partnerId) : undefined);
    const isSmallScreen = useIsSmallScreen();
    const isDuplicatingRef = React.useRef(false);
    //region State
    const [messageMetadata, setMessageMetadata] = React.useState<ProductDetailsState["messageMetadata"]>({
        posts: [],
        postId: undefined,
        threadId: undefined
    });
    const [messagerOpen, setMessagerOpen] = React.useState(false);
    const [messageSentDialogOpen, setMessageSentDialogOpen] = React.useState(false);
    const [confirmSaveDialogOpen, setConfirmSaveDialogOpen] = React.useState(false);
    const [showSaveBeforeVariantDialog, setShowSaveBeforeVariantDialog] = React.useState(false);
    const [variantDataToCreate, setVariantDataToCreate] = React.useState<ProductDetailsState["variantDataToCreate"]>(null);
    // AI Enrichment states
    const [aiEnrichmentDialogOpen, setAIEnrichmentDialogOpen] = React.useState(false);
    const [aiEnrichmentAttribute, setAIEnrichmentAttribute] = React.useState<ProductDetailsState["aiEnrichmentAttribute"]>(null);
    const [useV2Enrichment, setUseV2Enrichment] = React.useState(false);
    const [scoreDialogOpen, setScoreDialogOpen] = React.useState(false);
    const [productScore, setProductScore] = React.useState<ProductDetailsState["productScore"]>(null);
    const [productSuggestions, setProductSuggestions] = React.useState<ProductDetailsState["productSuggestions"]>([]);
    const [acceptedEnrichments, setAcceptedEnrichments] = React.useState<ProductDetailsState["acceptedEnrichments"]>(new Set());
    const [initProduct, setInitProduct] = React.useState<ProductDetailsState["initProduct"]>(defaultProduct);
    const [product, setProduct] = React.useState<ProductDetailsState["product"]>(defaultProduct);
    const [retailerProduct, setRetailerProduct] = React.useState<ProductDetailsState["retailerProduct"]>(null); // Used for product diff on supplier catalog //
    const [attributes, setAttributes] = React.useState<ProductDetailsState["attributes"]>([]);
    const [attributeSets, setAttributeSets] = React.useState<ProductDetailsState["attributeSets"]>([]);
    const [groupedAttributes, setGroupedAttributes] = React.useState<ProductDetailsState["groupedAttributes"]>({});
    const [availableAttributes, setAvailableAttributes] = React.useState<ProductDetailsState["availableAttributes"]>([]);
    const [tags, setTags] = React.useState<ProductDetailsState["tags"]>([]);
    const [loading, setLoading] = React.useState(true); // Show full on loading screen //
    const [updating, setUpdating] = React.useState(false); // Only show updating dialog //
    const [refreshing, setRefreshing] = React.useState(false); // Only show top progress bar //
    const [aiApplyCounter, setAiApplyCounter] = React.useState(0); // Bump to force AttributeInput resync after AI enrichment apply
    const [refresh, setRefresh] = React.useState(false);
    const [isDirty, setIsDirty] = React.useState(productId === undefined);
    const [error, setError] = React.useState<ProductDetailsState["error"]>({open: false, errorHeader: "", errorMessage: ""});
    const [allowVariations, setAllowVariations] = React.useState(false);
    const [finalizedProduct, setFinalizedProduct] = React.useState<ProductDetailsState["finalizedProduct"]>(null);
    const [approveDialogOpen, setApproveDialogOpen] = React.useState(false);
    const [rejectDialogOpen, setRejectDialogOpen] = React.useState(false);
    const [duplicateDialogOpen, setDuplicateDialogOpen] = React.useState(false);
    const [validationErrors, setValidationErrors] = React.useState<ProductDetailsState["validationErrors"]>({});
    const [variationValidationErrors, setVariationValidationErrors] = React.useState<ProductDetailsState["variationValidationErrors"]>([]);
    const [addVariationDialog, setAddVariationDialog] = React.useState(false);
    const [addVariationSkuDialog, setAddVariationSkuDialog] = React.useState(false);
    const [variationDeletionDialogOpen, setVariationDeletionDialogOpen] = React.useState(false);
    const [variationDeletionIndex, setVariationDeletionIndex] = React.useState<ProductDetailsState["variationDeletionIndex"]>(false);
    const [variesBy, setVariesBy] = React.useState<ProductDetailsState["variesBy"]>({options: [], fields: []});
    const [initVariesBy, setInitVariesBy] = React.useState<ProductDetailsState["initVariesBy"]>({options: [], fields: []});
    const [variations, setVariations] = React.useState<ProductDetailsState["variations"]>([]);
    const [initVariations, setInitVariations] = React.useState<ProductDetailsState["initVariations"]>([]);
    const [availableCategories, setAvailableCategories] = React.useState<ProductDetailsState["availableCategories"]>([]);
    const [supplierChannelData, setSupplierChannelData] = React.useState<ProductDetailsState["supplierChannelData"]>([]);
    const [snackbar, setSnackbar] = React.useState<ProductDetailsState["snackbar"]>({
        open: false,
        message: '',
        severity: 'success'
    });
    // POC System Settings //
    const [forceUniqueRetailerSku, setForceUniqueRetailerSku] = React.useState(false);

    //endregion
    // region Functions
    const updateProductState = React.useCallback((attribute, value) => {
        setProduct((prevState) => {
            // Only update if the state is different //
            if (prevState.Attributes[attribute] === value) return prevState;
            // If value is undefined, remove the attribute //
            if (value === undefined) {
                let newState = {...prevState};
                delete newState.Attributes[attribute];
                return newState;
            }
            return {
                ...prevState,
                Attributes: {
                    ...prevState.Attributes,
                    [attribute]: value
                }
            };
        });
    }, []);
    // AI Enrichment handlers
    const handleAIEnrichment = React.useCallback((attributeName) => {
        setUseV2Enrichment(false);
        setAIEnrichmentAttribute(attributeName);
        setAIEnrichmentDialogOpen(true);
    }, []);
    const handleAIEnrichmentApply = React.useCallback((attributeName, enrichedValue, metadata) => {
        // Update the product attribute with the enriched value
        updateProductState(attributeName, enrichedValue);
        // AttributeInput keeps an internal localValue for text/video/document fields and
        // its memo comparator suppresses re-renders for those types, so a prop value
        // change alone won't update the visible input. Bump aiApplyCounter to trigger
        // AttributeGroup -> forceRefresh -> useDidUpdateEffect resync of localValue.
        setAiApplyCounter(prev => prev + 1);
        if (useV2Enrichment) {
            setAcceptedEnrichments(prev => new Set(prev).add(attributeName));
        }
        // Close the dialog
        setAIEnrichmentDialogOpen(false);
        setAIEnrichmentAttribute(null);
    }, [updateProductState, useV2Enrichment]);
    const [rescoreMode, setRescoreMode] = React.useState(true);
    const handleScoreProduct = React.useCallback(() => {
        setRescoreMode(true);
        setScoreDialogOpen(true);
    }, []);
    const handleViewDetails = React.useCallback(() => {
        setRescoreMode(false);
        setScoreDialogOpen(true);
    }, []);

    const scoreReceivedRef = React.useRef(false);
    const handleScoreReceived = React.useCallback((score, suggestions) => {
        setProductScore(score);
        setProductSuggestions(suggestions);
        updateProductState('ai_score', String(Math.round(score * 10)));
        scoreReceivedRef.current = true;
        setAcceptedEnrichments(new Set());
    }, [updateProductState]);
    const handleScoreDialogClose = React.useCallback(() => {
        setScoreDialogOpen(false);
        if (scoreReceivedRef.current) {
            scoreReceivedRef.current = false;
            setSnackbar({ open: true, message: 'Score updated. Please save the product to apply changes.', severity: 'info' });
        }
    }, []);

    const handleScoreEnrichAttribute = React.useCallback((attributeName) => {
        setUseV2Enrichment(true);
        setAIEnrichmentAttribute(attributeName);
        setAIEnrichmentDialogOpen(true);
    }, []);
    const updateVariationState = React.useCallback((attributeName, attributeValue, index) => {
        // Only update if the state is different //
        if (variations[index].Attributes[attributeName] === attributeValue) return;
        setVariations((prevState) => {
            let newVariations = [...prevState];
            newVariations[index].Attributes[attributeName] = attributeValue;
            return newVariations;
        })
    }, [variations]);
    const updateGroupedAttributes = (attributes, attributeSets, currentAttributeSet) => {
        const parseGroupOrder = (groupName) => {
            // Parse format: {ORDER}.{Name of the display group}
            const match = groupName.match(/^(\d+)\.(.+)$/);
            if (match) {
                return {
                    order: parseInt(match[1], 10),
                    displayName: match[2].trim()
                };
            }
            // If no order prefix, return a high order number to put it at the end
            return {
                order: 999,
                displayName: groupName
            };
        };
        const groupAttributes = (attributes) => {
            let mediaAttributes = [];
            let groupedAttributes = {};
            let groupMetadata = {};
            attributes.forEach((attribute) => {
                if (attribute.Type === "image") {
                    mediaAttributes.push(attribute);
                } else {
                    const groupInfo = parseGroupOrder(attribute.Group);
                    let groupName = attribute.Group;
                    // If the parsed display name is "Basics", always use "Basics" as the group key
                    if (groupInfo.displayName === "Basics") {
                        groupName = "Basics";
                    }
                    // Store both the original name and parsed info
                    groupMetadata[groupName] = groupInfo;
                    groupedAttributes[groupName] = [...(groupedAttributes[groupName] || []), {
                        Name: attribute.Name,
                        FriendlyName: attribute.FriendlyName,
                        Type: attribute.Type,
                        Editor: attribute.Editor,
                        Options: attribute.Options,
                        Hidden: attribute.Hidden,
                        ValidationRules: attribute.ValidationRules
                    }];
                }
            });
            // Sort groups by order
            const sortedGroupNames = Object.keys(groupedAttributes).sort((a, b) => {
                const aInfo = groupMetadata[a] || parseGroupOrder(a);
                const bInfo = groupMetadata[b] || parseGroupOrder(b);
                // Special handling for "Basics" - always first
                if (aInfo.displayName === "Basics") return -1;
                if (bInfo.displayName === "Basics") return 1;
                return aInfo.order - bInfo.order;
            });
            // Create sorted grouped attributes
            let sortedGroupedAttributes = {};
            sortedGroupNames.forEach(groupName => {
                sortedGroupedAttributes[groupName] = groupedAttributes[groupName];
            });
            // Add media group at the end
            sortedGroupedAttributes["Media"] = mediaAttributes;
            return sortedGroupedAttributes;
        }
        // If no attribute set is selected, display attributes //
        let groupedAttributes = {};
        if (!currentAttributeSet || currentAttributeSet === "") {
            groupedAttributes = groupAttributes(attributes);
        } else {
            let usedAttributeSet = attributeSets.find((attributeSet) => attributeSet.Name === currentAttributeSet);
            if (usedAttributeSet == null) {
                setError({
                    open: true,
                    errorHeader: "Attribute Set Not Found!",
                    errorMessage: "The attribute set for this product was not found. Please contact support."
                });
                return;
            }
            let usedAttributes = attributes.filter((attribute) => usedAttributeSet.Attributes.includes(attribute.Name));
            if (usedAttributes.length === 0) {
                setError({
                    open: true,
                    errorHeader: "No Attributes Found!",
                    errorMessage: "No attributes were found for this attribute set. Please contact support."
                });
                return;
            }
            groupedAttributes = groupAttributes(usedAttributes);
        }
        // Filter out disallowed attribute types in other groups
        let filteredAttributes = {};
        Object.keys(groupedAttributes).forEach((group) => {
            let groupedAttribute = groupedAttributes[group];
            // if (group !== "Basics") // Filter out all image attributes from groups other than Basics //
            // groupedAttribute = groupedAttribute.filter((attribute) => attribute.Type !== "image");
            filteredAttributes[group] = groupedAttribute;
        });
        // Filter out disallowed attributes in all groups
        let disallowedAttributes = ["tags", "attribute_set", "approval_status", "sync_status", "image_metadata", "sku"];
        let allAttributesInSet = [];
        for (let key in filteredAttributes)
            allAttributesInSet.push(...filteredAttributes[key])
        allAttributesInSet = allAttributesInSet.filter((attribute) => !disallowedAttributes.includes(attribute.Name));
        setAvailableAttributes(allAttributesInSet);
        setGroupedAttributes(filteredAttributes);
    }
    const validateProduct = () => {
        let validationErrors: Record<string, string> = {};
        if (product.Attributes.attribute_set === "" || product.Attributes.attribute_set === undefined) {
            validationErrors.attribute_set = "Product attribute set is required!";
        }
        if (product.Attributes.sku === "" || product.Attributes.sku === undefined) {
            validationErrors.sku = "Product SKU is required!";
        }
        if (variations.length > 0 && (product.Attributes.parent_sku === "" || product.Attributes.parent_sku === undefined)) {
            validationErrors.parent_sku = "Parent SKU is required with variants!";
        }
        // Run validation rules for attributes to check if they are valid //
        Object.assign(validationErrors, RunAttributeRules(availableAttributes, product.Attributes));
        setValidationErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) {
            const errorMessages = Object.keys(validationErrors).map((key) => validationErrors[key]);
            const message = "Please fix the errors with your product before saving.\n- " + errorMessages.join("\n- ");
            setError({
                open: true,
                errorHeader: "Errors with product!",
                errorMessage: message
            });
        }
        return Object.keys(validationErrors).length <= 0;
    }
    const validateVariations = () => {
        let errors: Array<Record<string, string>> = [];
        variations.forEach((variation) => {
            let rowErrors: Record<string, string> = {}
            if (variation.Attributes.sku === "" || variation.Attributes.sku === undefined)
                rowErrors.sku = "SKU is required for all variations!"
            errors.push(rowErrors)
        });
        setVariationValidationErrors(errors);
        if (errors.some((error) => Object.keys(error).length > 0)) {
            setError({
                open: true,
                errorHeader: "Errors with variations!",
                errorMessage: "Please fix the errors with your variations before saving."
            });
            return false;
        }
        return true;
    }
    const formatVariations = (variations, product) => {
        const nonCopiedAttributes = ["approval_status", "sync_status", "tags", "approved_by", "listed_by", "delisted_by", "approval_date", "created_date", "listed_date", "delisted_date"];
        // In JS objects are shared, so basically this function is pass by reference //
        if (variations.length > 0) {
            // Update variesBy & variantFields //
            product.Attributes.varies_by = variesBy.options.map((option) => option.value);
            product.Attributes.variant_fields = (variesBy.fields ?? []).map((field) => field.value);
            variations.forEach((variation) => {
                let variantAttributes = variesBy.options.map((option) => option.value).concat((variesBy.fields ?? []).map((field) => field.value));
                variantAttributes.push("sku"); // SKU is always a variant attribute //
                let updateAttributes = cloneDeep(product.Attributes);
                for (const attribute of variantAttributes) {
                    if (variation.Attributes[attribute] !== undefined)
                        updateAttributes[attribute] = variation.Attributes[attribute];
                }
                for (const attribute of nonCopiedAttributes) {
                    if (variation.Attributes[attribute] !== undefined)
                        updateAttributes[attribute] = variation.Attributes[attribute];
                }
                variation.Attributes = updateAttributes;
            })
        }
    }
    const createVariations = async (variations) => {
        let errors = [];
        if (!hasInventoryManagePermission) {
            setError({
                open: true,
                errorHeader: "Permission Denied",
                errorMessage: "You do not have permission to perform this action."
            })
            return;
        }
        await Promise.all(variations.map(async (variation) => {
            let response = await utilCreateProduct({...variation}, partnerId, props.isRetailer);
            if (response.errorMessage) {
                errors.push({ sku: variation.Attributes.sku, errorMessage: response.errorMessage });
            }
            return response;
        }));
        return errors;
    }
    const updateVariations = async (variations) => {
        let errors = [];
        if (!hasInventoryManagePermission) {
            setError({
                open: true,
                errorHeader: "Permission Denied",
                errorMessage: "You do not have permission to perform this action."
            })
            return;
        }
        const chunkArray = (arr, size) => {
            const result = [];
            for (let i = 0; i < arr.length; i += size) {
                result.push(arr.slice(i, i + size));
            }
            return result;
        };
        const batches = chunkArray(variations, 10);
        for (const batch of batches) {
            await Promise.all(batch.map(async (variation) => {
                let response = await utilUpdateProduct(
                    { ...variation },
                    variation.Attributes.sku,
                    partnerId,
                    catalog === "partnercatalog",
                    props.isRetailer
                );
                if (response.errorMessage) {
                    errors.push({ sku: variation.Attributes.sku, errorMessage: response.errorMessage });
                }
                return response;
            }));
            await new Promise(res => setTimeout(res, 1000));
        }
        return errors;
    }
    const deleteVariations = async (variations: ProductVariant[]) => {
        if (!hasInventoryManagePermission) {
            setError({
                open: true,
                errorHeader: "Permission Denied",
                errorMessage: "You do not have permission to perform this action."
            })
            return;
        }
        await Promise.all(variations.map(async (variation) => {
            let response: any = await utilDeleteProduct(variation.Attributes.sku, partnerId, catalog === "partnercatalog", props.isRetailer);
            if (response.errorMessage) {
                setError(response as any);
                console.error("Error deleting variation! Please contact support with this message: " + response.errorMessage);
            }
            return response;
        }));
    }
    const duplicateProduct = async () => {
        if (!hasInventoryManagePermission) {
            setError({
                open: true,
                errorHeader: "Permission Denied",
                errorMessage: "You do not have permission to perform this action."
            })
            return;
        }
        isDuplicatingRef.current = true;
        setProductId(undefined);
        setProduct({...product, Attributes: {...product.Attributes, sku: "", sync_status: ""}});
        setInitProduct({...initProduct, Attributes: {...initProduct.Attributes, sku: "", sync_status: ""}});
        setIsDirty(true);
        props.history.replace(props.location.pathname + props.location.search.replace(`&productId=${productId}`, ""));
        setDuplicateDialogOpen(true);
    }
    const createProduct = async () => {
        if (!hasInventoryManagePermission) {
            setError({
                open: true,
                errorHeader: "Permission Denied",
                errorMessage: "You do not have permission to perform this action."
            })
            return;
        }
        if (!validateProduct())
            return;
        if (!validateVariations())
            return;
        let productToUpload = cloneDeep(product);
        let variationsToUpload = cloneDeep(variations);
        formatVariations(variationsToUpload, productToUpload)
        setUpdating(true);
        // Update base product //
        let response = await utilCreateProduct({...productToUpload}, partnerId, props.isRetailer);
        if (response.errorMessage) {
            setError(response);
            setUpdating(false);
            return;
        }
        // Create any new variations //
        let createErrors = [];
        let newVariations = variationsToUpload.filter((variation) => variation.isNewVariation);
        if (newVariations.length > 0) createErrors = await createVariations(newVariations);
        if (createErrors.length > 0) {
            setError({
                open: true,
                errorHeader: "Error saving variations!",
                errorMessage: createErrors.map(e => `${e.sku}: ${e.errorMessage}`).join("\n")
            });
        } else {
            // Show success message if variants were created
            if (newVariations.length > 0) {
                setSnackbar({
                    open: true,
                    message: `${newVariations.length} variant${newVariations.length > 1 ? 's' : ''} created successfully!`,
                    severity: 'success'
                });
            }
            props.history.replace(props.location.pathname + props.location.search + "&productId=" + response);
        }
        setProduct(productToUpload);
        setVariations(variationsToUpload);
        setRefresh(!refresh);
        setRefreshing(true);
        setUpdating(false);
    }
    const updateProduct = async (applyToVariants) => {
        const updateProductStatus = (initProduct, product) => {
            if (props.isRetailer) {
                if (catalog === "mycatalog") {
                    if (initProduct.Attributes.sync_status !== "Delisted" && initProduct.Attributes.sync_status !== "Not Listed" && initProduct.Attributes.sync_status !== "Delist Pending")
                        product.Attributes.sync_status = "Listing Pending";
                } else {
                    if (initProduct.Attributes.approval_status === product.Attributes.approval_status)
                        product.Attributes.approval_status = "Under Review";
                }
            } else {
                product.Attributes.approval_status = "Update Suggested";
            }
        }
        if (!hasInventoryManagePermission) {
            setError({
                open: true,
                errorHeader: "Permission Denied",
                errorMessage: "You do not have permission to perform this action."
            })
            return;
        }
        if (!validateProduct())
            return;
        if (!validateVariations())
            return;
        let productToUpload = cloneDeep(product);
        // Retailer Listing and Approval Workflow //
        if (!isEqual(initProduct, product)) {
            updateProductStatus(initProduct, productToUpload);
        }
        // Supplier Listing and Approval Workflow //
        if ((!props.isRetailer || (props.isRetailer && catalog === "partnerCatalog")) && productToUpload.Attributes.sync_status) {
            if (initProduct.Attributes.sync_status === productToUpload.Attributes.sync_status)
                productToUpload.Attributes.sync_status = "Listing Pending";
        }
        let variationsToUpload = cloneDeep(variations);
        // Ensure varies_by and variant_fields are up to date before saving
        formatVariations(variationsToUpload, productToUpload);
        if (variationsToUpload.length > 0) {
            // Get variesBy and variantFields attributes
            const variesByAttributes = (variesBy.options || []).map(option => option.value);
            const variantFields = (variesBy.fields || []).map(field => field.value);
            const excludedForFalse = new Set([...(variesByAttributes || []), ...(variantFields || []), 'sku']);
            const excludedForTrue = new Set(['sku']);
            // Find changed attributes on the base product
            const changedAttributes = Object.keys(productToUpload.Attributes).filter(attr =>
                !isEqual(productToUpload.Attributes[attr], initProduct.Attributes[attr])
            );
            variationsToUpload.forEach(variation => {
                changedAttributes.forEach(attr => {
                    if (applyToVariants) {
                        // Copy all changed except 'sku'
                        if (!excludedForTrue.has(attr)) {
                            variation.Attributes[attr] = productToUpload.Attributes[attr];
                        }
                    } else {
                        // Copy all changed except variesBy, variantFields, and 'sku'
                        if (!excludedForFalse.has(attr)) {
                            variation.Attributes[attr] = productToUpload.Attributes[attr];
                        }
                    }
                });
            });
        }
        setUpdating(true);
        // Update base product //
        let runStatus = await utilUpdateProduct({...productToUpload}, productId, partnerId, catalog === "partnercatalog", props.isRetailer);
        if (runStatus.errorMessage) {
            setError(runStatus);
            setUpdating(false);
            return;
        }
        let createErrors = [];
        let updateErrors = [];
        const shouldUpdateVariations = variesBy.options && variesBy.options.length > 0;
        // Create any new variations //
        let newVariations = variationsToUpload.filter((variation) => variation.isNewVariation);
        if (newVariations.length > 0) createErrors = await createVariations(newVariations);
        // Update any old variations //
        let oldVariations = variationsToUpload.filter((variation) => !variation.isNewVariation);
        if (oldVariations.length > 0 && shouldUpdateVariations) updateErrors = await updateVariations(oldVariations);
        if (createErrors.length > 0 || updateErrors.length > 0) {
            setError({
                open: true,
                errorHeader: "Error saving variations!",
                errorMessage: [
                    ...updateErrors.map(e => `${e.sku}: ${e.errorMessage}`),
                    ...createErrors.map(e => `${e.sku}: ${e.errorMessage}`)
                ].join("\n")
            });
        } else {
            // Show success message if new variants were created
            if (newVariations.length > 0) {
                setSnackbar({
                    open: true,
                    message: `${newVariations.length} new variant${newVariations.length > 1 ? 's' : ''} created successfully!`,
                    severity: 'success'
                });
            }
            // Remove old productId from URL and replace with new one //
            props.history.replace(`${props.location.pathname}${props.location.search.replace(`productId=${productId}`, `productId=${productToUpload.Attributes.sku}`)}`);
            // Update product state //
            unstable_batchedUpdates(() => {
                setProductId(productToUpload.Attributes.sku);
                setProduct(productToUpload);
                setVariations(variationsToUpload);
                setRefresh(!refresh);
                setRefreshing(true);
            });
        }
        setUpdating(false);
    }
    const delistProduct = async () => {
        if (!hasInventoryManagePermission) {
            setError({
                open: true,
                errorHeader: "Permission Denied",
                errorMessage: "You do not have permission to perform this action."
            })
            return;
        }
        let productToUpload = product;
        if (!validateProduct())
            return;
        productToUpload.Attributes.sync_status = "Delist Pending";
        productToUpload.Attributes.delisted_by = Auth.getUsername();
        productToUpload.Attributes.delisted_date = moment().format("YYYY-MM-DD");
        setUpdating(true);
        let runStatus = await utilUpdateProduct({...productToUpload}, product.Attributes.sku, partnerId, catalog === "partnercatalog", props.isRetailer);
        if (runStatus.errorMessage)
            setError(runStatus);
        await new Promise(r => setTimeout(r, 750));
        setUpdating(false);
        // window.location.reload();
    }
    const listProduct = async () => {
        if (!hasInventoryManagePermission) {
            setError({
                open: true,
                errorHeader: "Permission Denied",
                errorMessage: "You do not have permission to perform this action."
            })
            return;
        }
        let productToUpload = product;
        if (!validateProduct())
            return;
        productToUpload.Attributes.sync_status = "Listing Pending";
        productToUpload.Attributes.listed_by = Auth.getUsername();
        productToUpload.Attributes.listed_date = moment().format("YYYY-MM-DD");
        setUpdating(true);
        let runStatus = await utilUpdateProduct({...productToUpload}, product.Attributes.sku, partnerId, catalog === "partnercatalog", props.isRetailer);
        if (runStatus.errorMessage)
            setError(runStatus);
        await new Promise(r => setTimeout(r, 750));
        setUpdating(false);
        // window.location.reload();
    }
    const refreshPosts = async () => {
        const response = await getPosts(productId, "POC", partnerId, coid, props.isRetailer);
        if (response.errorMessage) {
            setError(response);
        } else {
            if (response.length === 0) return;
            // Ensure this post is for current partner //
            const threadDetails = await getThreadDetails(response[0].Id);
            // eslint-disable-next-line
            if (threadDetails.ReceiptDetails.length !== 0 && threadDetails.ReceiptDetails[0].Receiver != partnerId)
                return;
            let allMessages = response.map(async (post) => {
                let messagesThread = await getPostThreads(post.Id, productId, partnerId, coid, props.isRetailer);
                return [...messagesThread, post];
            });
            const results = await Promise.all(allMessages);
            setMessageMetadata({
                posts: results.flat(),
                postId: null,
                threadId: null
            });
        }
    }
    const handleAddVariationSku = async (variantData) => {
        // Check if there are unsaved changes to the parent product
        if (isDirty) {
            // Show dialog to ask user if they want to save parent product first
            setAddVariationSkuDialog(false);
            setVariantDataToCreate(variantData);
            setShowSaveBeforeVariantDialog(true);
            return;
        }
        // Proceed with creating the variant
        await createVariantAndNavigate(variantData);
    };
    const createVariantAndNavigate = async (variantData) => {
        setUpdating(true);
        let newVariation = cloneDeep(product);
        // Apply all the variant data to the new variation
        Object.keys(variantData).forEach(key => {
            newVariation.Attributes[key] = variantData[key];
        });
        // Create the variant via API
        let response = await utilCreateProduct({...newVariation}, partnerId, props.isRetailer);
        if (response && response.errorMessage) {
            // Ensure errorMessage is a string, not an object
            const errorMsg = typeof response.errorMessage === 'object' 
                ? JSON.stringify(response.errorMessage) 
                : response.errorMessage;
            setError({
                open: true,
                errorHeader: response.errorHeader || "Error creating variant!",
                errorMessage: errorMsg
            });
            setUpdating(false);
            return;
        }
        // Get the new variant ID from the response
        // Ensure we have a string ID, not an object
        const newVariantId = typeof response === 'object' ? (response.Id || response.id || response) : response;
        // Show success snackbar
        setSnackbar({
            open: true,
            message: 'Variant created successfully! You\'ve been redirected to the new variant.',
            severity: 'success'
        });
        // Navigate to the newly created variant
        let newUrl = `/productonboardingcenter/product?catalog=${catalog}&productId=${encodeURIComponent(newVariantId)}`;
        if (catalog === "partnercatalog" || (catalog === "mycatalog" && !props.isRetailer)) {
            newUrl += `&partnerId=${partnerId}`;
        }
        props.history.replace(newUrl);
        // Trigger a refresh to ensure the component re-reads URL parameters
        setRefresh(!refresh);
        setUpdating(false);
    };
    //endregion
    //region Lifecycle
    useEffect(() => {
        if(setTitle && product.Attributes && product.Attributes.name) {
            setTitle(`POC: ${product.Attributes.name}`, true);
        }
    }, [product.Attributes, setTitle]);
    useEffect(() => {
        if (isDuplicatingRef.current) {
            isDuplicatingRef.current = false;
            return;
        }
        refreshPosts();
        const urlProductId = new Helpers().queryString(props.location.search)["productId"];
        const urlPartnerId = new Helpers().queryString(props.location.search)["partnerId"];
        const urlCatalog = new Helpers().queryString(props.location.search)["catalog"];
        if (urlCatalog == null) {
            setError({
                open: true,
                errorHeader: "No catalog reference provided!",
                errorMessage: "Please ensure you are opening a product from one of the catalogs, or provide a catalog reference in the URL." +
                    "\n\nExample: /productonboardingcenter/product?catalog=retailer&productId=12345"
            })
        } else if (urlProductId == null && urlCatalog === "partnercatalog" && props.isRetailer) {
            setError({
                open: true,
                errorHeader: "Creating new products for suppliers is unsupported!",
                errorMessage: "To create a new product please navigate to the 'My Catalog' section and select the 'Add new item' button."
            })
        } else {
            // TODO: Once API calls are moved to utils remove this
            let variationsUrl = "";
            let variationProductUrl = "";
            if (props.isRetailer) {
                if (urlCatalog === "mycatalog") {
                    variationsUrl = retailerCatalogSearch;
                    variationProductUrl = Config.api + `/api/v2/ProductOnboardingCenter/Products`;
                } else {
                    variationsUrl = supplierCatalogSearch;
                    variationProductUrl = Config.api + `/api/v2/ProductOnboardingCenter/${urlPartnerId}/SupplierProducts`;
                }
            } else {
                if (urlCatalog === "mycatalog") {
                    variationsUrl = supplierCatalogSearch;
                    variationProductUrl = Config.api + `/api/v2/ProductOnboardingCenter/${urlPartnerId}/SupplierProducts`;
                } else {
                    variationsUrl = retailerCatalogSearch + `?merchantId=${urlPartnerId}`;
                    variationProductUrl = Config.api + `/api/v2/ProductOnboardingCenter/${urlPartnerId}/Products`;
                }
            }
            // region API Calls
            const getProduct = async () => {
                let decodedProductId = decodeURIComponent(urlProductId);
                return await utilGetProduct(decodedProductId, urlPartnerId, urlCatalog === "partnercatalog", props.isRetailer);
            }
            const getVariations = async (baseProduct) => {
                try {
                    const getErrors = [];
                    const fullVariations = [];
                    let query = (!props.isRetailer && urlCatalog === "partnercatalog" ? "&" : "?")
                        + `$filter=parentSku eq '${baseProduct.Attributes.parent_sku}'`;
                    if (props.isRetailer && urlCatalog === "partnercatalog")
                        query += `and supplierId eq ${urlPartnerId}`;
                    const merchantIdNum = Number(urlPartnerId);
                    if (!props.isRetailer && urlCatalog === "mycatalog" && urlPartnerId != null && urlPartnerId !== "" && Number.isFinite(merchantIdNum))
                        query += ` and merchantId eq ${merchantIdNum}`;
                    const response = await axios.get(variationsUrl + query);
                    let variations = response.data.value;
                    variations = variations.filter((variation) => variation.sku !== baseProduct.Attributes.sku);
                    await Promise.all(variations.map(async (variation) => {
                        await axios.get(variationProductUrl + "?id=" + variation.sku)
                            .then(response => {
                                fullVariations.push(response.data);
                            })
                            .catch(error => {
                                getErrors.push(error);
                            });
                    }));
                    if (getErrors.length > 0) {
                        setError({
                            open: true,
                            errorHeader: "Error getting some variations!",
                            errorMessage: getErrors.map((error) => error.response.data.Message).join("\n")
                        });
                    }
                    return fullVariations;
                } catch (error) {
                    let errorMessage = error.message;
                    if (error.response != null) errorMessage = error.response.data.Message;
                    setError({
                        open: true,
                        errorHeader: "Error getting variations!",
                        errorMessage: errorMessage
                    });
                    return [];
                }
            }
            const getAttributes = async () => {
                return await utilGetAttributes(urlPartnerId, props.isRetailer);
            }
            const getAttributeSets = async () => {
                return utilGetAttributeSets(urlCatalog === "partnercatalog", urlPartnerId, undefined, props.isRetailer);
            }
            const getAllProductTags = async () => {
                return utilGetAllTags(props.currentCatalog === "partnercatalog", props.isRetailer);
            }
            // endregion
            const getPocSystemSettings = async () => {
                try {
                    const settings = (await axios.get(Config.api + `/odata/Company/Functions.GetSystemSettings?system=ProductOnboardingCenter`)).data || {};
                    setForceUniqueRetailerSku(settings.POCForceUniqueRetailerSku === true || settings.POCForceUniqueRetailerSku === 'true');
                } catch (e) {
                    // non-critical, default to false
                }
            }
            const loadData = async () => {
                // setLoading(true); Commented out on purpose to prevent loading screen from showing on refresh //
                const [foundProduct, foundAttributes, foundAttributeSets, tags] = await Promise.all([
                    urlProductId !== undefined ? getProduct() : Promise.resolve(defaultProduct),
                    getAttributes(),
                    getAttributeSets(),
                    getAllProductTags(),
                    getPocSystemSettings(),
                ]);
                if (foundProduct.errorMessage || foundAttributes.errorMessage || foundAttributeSets.errorMessage || tags.errorMessage) {
                    setError({
                        open: true,
                        errorHeader: "Error loading product!",
                        errorMessage:
                            foundProduct.errorMessage ? "Product errors: " + foundProduct.errorMessage + "\n" : "" +
                            foundAttributes.errorMessage ? "Attribute errors: " + foundAttributes.errorMessage + "\n" : "" +
                            foundAttributeSets.errorMessage ? "Attribute set errors: " + foundAttributeSets.errorMessage + "\n" : "" +
                            tags.errorMessage ? "Tag errors: " + tags + "\n" : ""
                    })
                    return;
                }
                // Format variations & VariesBy, VariantFields //
                if (urlProductId !== undefined) {
                    const fullVariations = await getVariations(foundProduct);
                    setVariations(fullVariations);
                    setInitVariations(cloneDeep(fullVariations));
                    if (foundProduct.Attributes.varies_by != null) {
                        let optionsToRender = [...foundProduct.Attributes.varies_by];
                        let fieldsToRender = [...(foundProduct.Attributes?.variant_fields ?? [])];
                        // Limit foundProduct.Attributes.varies_by to 25 //
                        // TODO(ENG-344): looks like a pre-existing bug - comparing array to number, but preserving original behavior
                        if ((optionsToRender as any) > 25) {
                            optionsToRender = optionsToRender.slice(0, 25);
                        }
                        // Limit foundProduct.Attributes.variant_fields to 25 //
                        if (fieldsToRender.length > 25) {
                            fieldsToRender = fieldsToRender.slice(0, 25);
                        }
                        let variesBy = {
                            options: optionsToRender.map((option) => {
                                let foundAttribute = foundAttributes.find((attribute) => attribute.Name === option);
                                return {
                                    label: foundAttribute === undefined
                                        ? (
                                            <Tooltip title={"Attribute not found, please create it in settings!"}>
                                                <Typography style={{color: "red"}}>{option}</Typography>
                                            </Tooltip>
                                        )
                                        : foundAttribute.FriendlyName,
                                    value: option,
                                    type: foundAttribute?.Type ?? ""
                                }
                            }),
                            fields: fieldsToRender.map((field) => {
                                let foundAttribute = foundAttributes.find((attribute) => attribute.Name === field);
                                return {
                                    label: foundAttribute === undefined
                                        ? (
                                            <Tooltip title={"Attribute not found, please create it in settings!"}>
                                                <Typography component={'span'}
                                                            style={{color: "red"}}>{field}</Typography>
                                            </Tooltip>
                                        )
                                        : foundAttribute.FriendlyName,
                                    value: field,
                                    type: foundAttribute?.Type ?? ""
                                }
                            }),
                        };
                        setVariesBy(variesBy);
                        setInitVariesBy({...variesBy});
                    }
                }
                ///////////////////////
                unstable_batchedUpdates(() => {
                    setInitProduct(cloneDeep(foundProduct));
                    setProduct(foundProduct);
                    setAttributes(foundAttributes);
                    setAttributeSets(foundAttributeSets);
                    setTags(tags);
                    if (foundProduct.Attributes && foundProduct.Attributes.ai_score) {
                        setProductScore(parseInt(foundProduct.Attributes.ai_score, 10) / 10);
                    }
                })
                updateGroupedAttributes(foundAttributes, foundAttributeSets, foundProduct.Attributes.attribute_set);
                if (!error.open) setLoading(false);
                setRefreshing(false);
            }
            const loadChannelData = async () => {
                const partners = await utilGetPartners();
                const inventory = await utilSearchInventory([productId]);
                const product = await utilSearchSupplierCatalog([productId]);
                let quantities = {};
                let warehouses = {};
                let products = {};
                if (inventory.success) {
                    inventory.data.forEach(item => {
                        quantities[item.merchantId] = item.quantity;
                        warehouses[item.merchantId] = item.warehouse;
                    })
                }
                if (product.success) {
                    product.data.forEach(item => {
                        products[item.merchantId] = item;
                    })
                }
                const channelData = props.retailers.map(retailer => {
                    // eslint-disable-next-line
                    let foundPartner = partners.find(partner => partner.Id == retailer.Id);
                    return {
                        id: foundPartner?.Id ?? "Unknown",
                        name: foundPartner?.CompanyName ?? "Unknown",
                        warehouse: warehouses[foundPartner.Id],
                        quantity: quantities[foundPartner.Id] ?? 0,
                        syncStatus: products[foundPartner.Id]?.syncStatus ?? "Not Listed",
                        foundProduct: products[foundPartner.Id]
                    }
                });
                setSupplierChannelData(channelData ?? []);
            }
            unstable_batchedUpdates(() => {
                setIsDirty(urlProductId === undefined);
                if (urlProductId !== undefined) setProductId(decodeURIComponent(urlProductId));
                setPartnerId(urlPartnerId);
                setCatalog(urlCatalog);
            })
            loadData();
            if (!props.isRetailer && productId) loadChannelData()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refresh, props.location.search])
    useEffect(() => {
        if (catalog != null) {
            const currentAttributeSet = attributeSets.find((attributeSet) => attributeSet.Name === product.Attributes.attribute_set);
            let allowVariations = currentAttributeSet == null ? false
                : (currentAttributeSet.Attributes.includes("parent_sku")
                    && currentAttributeSet.Attributes.includes("varies_by"))
            setAllowVariations(allowVariations);
            // On attribute set change, update grouped attributes for rerender //
            if (attributeSets.length > 0 && attributes.length > 0)
                updateGroupedAttributes(attributes, attributeSets, product.Attributes.attribute_set);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [product.Attributes.attribute_set]);
    useEffect(() => {
        if (loading || refreshing) return;
        // Logic set like this to prevent unnecessary rerenders //
        if (productId !== undefined) {
            if ((!isEqual(product, initProduct)
                    || !isEqual(variations, initVariations)
                    || !isEqual(variesBy, initVariesBy))
                && !isDirty)
                setIsDirty(true);
            else if ((isEqual(product, initProduct)
                    && isEqual(variations, initVariations)
                    && isEqual(variesBy, initVariesBy))
                && isDirty)
                setIsDirty(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [product, variations, variesBy, refresh])
    useEffect(() => {
        const getCategories = async () => {
            let runStatus = await utilGetCategories(partnerId, props.isRetailer);
            if (runStatus.errorMessage) setAvailableCategories([]);
            else setAvailableCategories(runStatus);
        }
        const getRetailerProduct = async () => {
            // Get retailer product for diff checking
            // Reach out to matching file for retailer product sku
            let foundMerchantSku = null;
            const foundInventory = await utilCheckInventory(partnerId, productId);
            if (foundInventory.errorStatus === 404) {
                console.warn("Warning: No inventory found for retailer product!");
            } else if (foundInventory.errorMessage) {
                console.error("Error: loading retailer product for attribute compare! " + foundInventory.errorMessage);
            } else {
                foundMerchantSku = foundInventory.MerchantSKU;
                if (isNullOrEmpty(foundMerchantSku)) {
                    foundMerchantSku = null;
                }
            }
            let runStatus = await utilGetProduct(foundMerchantSku ?? productId, undefined, false, props.isRetailer);
            if (runStatus.errorMessage) {
                runStatus.errorMessage = "Error loading retailer product for attribute compare! " + runStatus.errorMessage;
                if (runStatus.errorStatus !== 404)
                    setError(runStatus)
            } else {
                setRetailerProduct(runStatus)
            }
        }
        getCategories();
        if (catalog === "partnercatalog" && props.isRetailer) getRetailerProduct();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    //endregion
    return (
        <form>
            <LinearProgress

                style={{
                    width: "calc(100% + 2em)",
                    height: '0.4em',
                    position: "relative",
                    top: "-1em",
                    right: "1em",
                    visibility: (loading || refreshing) ? undefined : 'hidden'
                }}
                {...({type: 'indeterminate'} as any)}/>
            <Grid container justifyContent={"space-between"}>
                <Grid container item size={3} style={{
                    flexDirection: "column",
                    paddingRight: "10px"
                }}>
                    {(!props.isRetailer && supplierChannelData.length !== 0) && (
                        <Grid container>
                            <Typography style={{padding: "8px", fontSize: "24px", fontWeight: 600}}>
                                {isPartnerMarketplace ? "Channels" : "Retailers"}
                            </Typography>
                            {(!loading && supplierChannelData.some((channel) => channel.warehouse)) && (
                                <Alert icon={false} severity={"info"}>
                                    <Typography variant={"body1"} style={{textAlign: "left"}}>
                                        The Quantity field below reflects total inventory levels.
                                        This product has different inventory levels available by warehouse.
                                        To manage these individually, see <Link
                                        href={"/advanced-product-management/search"} target="_blank" rel="noopener">Inventory
                                        Search</Link> and to manage these in bulk, see <Link
                                        href={"/advanced-product-management"} target="_blank" rel="noopener">Inventory
                                        Feeds</Link>.
                                    </Typography>
                                </Alert>
                            )}
                            {loading ? (
                                <List preserveAspectRatio='none'
                                      style={{height: '200px', width: '100%', marginLeft: "10px"}}
                                      primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>
                            ) : (
                                <Grid item container
                                      style={{
                                          padding: "8px",
                                          gap: "16px",
                                          flexDirection: "column",
                                          maxHeight: "500px",
                                          overflow: "auto"
                                      }}>
                                    <Table>
                                        <TableHead>
                                            <TableCell style={{padding: 0}}>
                                                Name
                                            </TableCell>
                                            <TableCell style={{padding: "0px 5px"}}>
                                                Quantity
                                            </TableCell>
                                            <TableCell>
                                            </TableCell>
                                            {!isSmallScreen && (
                                                <TableCell>
                                                </TableCell>
                                            )}
                                        </TableHead>
                                        <TableBody>
                                            {supplierChannelData
                                                .sort((a, b) => {
                                                    if (a.foundProduct && !b.foundProduct) return -1;
                                                    if (!a.foundProduct && b.foundProduct) return 1;
                                                    return 0;
                                                })
                                                .map((channel, index) => {
                                                    if (channel.foundProduct) {
                                                        return (
                                                            <TableRow key={index} style={{
                                                                // eslint-disable-next-line
                                                                backgroundColor: channel.id == partnerId ? "#e0e0e0" : "transparent",
                                                                cursor: isSmallScreen ? "pointer" : "default"
                                                            }}
                                                                      onClick={() => {
                                                                          if (isSmallScreen)
                                                                              window.location.href = `/productonboardingcenter/product?catalog=mycatalog&productId=${productId}&partnerId=${channel.id}`;
                                                                      }}
                                                            >
                                                                <TableCell style={{padding: 0}}>
                                                                    {channel.name}
                                                                </TableCell>
                                                                <TableCell style={{padding: "0px 5px"}}>
                                                                    {channel.quantity}
                                                                </TableCell>
                                                                <TableCell style={{padding: 0, width: "30px"}}>
                                                                    <Grid container justifyContent={"center"}>
                                                                        {channel.syncStatus && (
                                                                            <Chip
                                                                                label={channel.syncStatus}
                                                                                size={"small"}
                                                                                style={{
                                                                                    width: "75px",
                                                                                    height: "25px",
                                                                                    color: "white",
                                                                                    backgroundColor: listingStatusColorMapper(channel.syncStatus)
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </Grid>
                                                                </TableCell>
                                                                {!isSmallScreen && (
                                                                    <TableCell style={{padding: 0}}>
                                                                        {/* eslint-disable-next-line eqeqeq */}
                                                                        {channel.id != partnerId && (
                                                                            <Grid container justifyContent={"flex-end"}>
                                                                                <Button
                                                                                    variant={"outlined"}
                                                                                    style={{
                                                                                        width: "25px",
                                                                                        height: "25px"
                                                                                    }}
                                                                                    href={`/productonboardingcenter/product?catalog=mycatalog&productId=${productId}&partnerId=${channel.id}`}
                                                                                    component={"a"}
                                                                                >
                                                                                    View
                                                                                </Button>
                                                                            </Grid>
                                                                        )}
                                                                    </TableCell>
                                                                )}
                                                            </TableRow>
                                                        );
                                                    }
                                                    return (
                                                        <TableRow key={index}>
                                                            <TableCell style={{padding: 0}}>
                                                                {channel.name}
                                                            </TableCell>
                                                            <TableCell style={{padding: "0px 5px"}}>
                                                            </TableCell>
                                                            <TableCell style={{padding: 0, width: "30px"}}>
                                                            </TableCell>
                                                            {!isSmallScreen && (
                                                                <TableCell style={{padding: 0}}>
                                                                    <Grid container justifyContent={"flex-end"}>
                                                                        <Button
                                                                            variant={"outlined"}
                                                                            style={{
                                                                                width: "25px",
                                                                                height: "25px"
                                                                            }}
                                                                            // link to import
                                                                            href={"/productonboardingcenter/import?partnerId=" + channel.id}
                                                                            component={"a"}
                                                                        >
                                                                            Add
                                                                        </Button>
                                                                    </Grid>
                                                                </TableCell>
                                                            )}
                                                        </TableRow>
                                                    );
                                                })}
                                        </TableBody>
                                    </Table>
                                </Grid>
                            )}
                        </Grid>
                    )}
                    <Grid container style={{
                        position: "sticky",
                        top: 0,
                        flexDirection: "column",
                        alignItems: "flex-start",
                        height: "calc(100vh - 60px)",
                        overflow: "auto"
                    }}>
                        <Typography style={{flex: 0, padding: "8px", fontSize: "24px", fontWeight: 600}}>
                            Navigation
                        </Typography>
                        {loading ? (
                            <List preserveAspectRatio='none'
                                  style={{height: '200px', width: '100%', marginLeft: "10px"}}
                                  primaryColor='#e1e1e1' secondaryColor='#d8d8d8'/>
                        ) : (
                            <Grid item container
                                  style={{
                                      flex: 1,
                                      padding: "8px",
                                      gap: "16px",
                                      flexDirection: "column",
                                      overflow: "auto",
                                      textAlign: "left",
                                      alignItems: "flex-start",
                                      flexWrap: "nowrap"
                                  }}>
                                {groupedAttributes && Object.keys(groupedAttributes)
                                    .filter((group) => group !== "Media")
                                    .map((group, index) => {
                                        const cleanedGroupAttributes = cleanGroupAttributes(groupedAttributes[group], product);
                                        if (cleanedGroupAttributes.length === 0) return null;
                                        return (
                                            <React.Fragment key={index}>
                                                {groupedAttributes[group].length > 0 && (
                                                    <NavigationLink key={index} onClick={() => scrollToGroup(group)}
                                                                    title={group.match(/^(\d+)\.(.+)$/) ? group.match(/^(\d+)\.(.+)$/)[2].trim() : group}/>
                                                )}
                                                {/*Manually render media link */}
                                                {group === "Basics" && groupedAttributes["Media"].filter((attribute) => attribute.Type === "image").length > 0 && (
                                                    <NavigationLink key={"media"} onClick={() => scrollToGroup("Media")}
                                                                    title={"Media"}/>
                                                )}
                                                {/*Manually render variations link */}
                                                {/* {group === "Basics" && allowVariations && (
                                                    <NavigationLink key={"variations"}
                                                                    onClick={() => scrollToGroup("Variations")}
                                                                    title={"Variations"}/>
                                                )} */}
                                            </React.Fragment>
                                        );
                                    })}
                            </Grid>
                        )}
                    </Grid>
                </Grid>
                <Grid container item size={9} direction={"column"}>
                    <Grid container item size={12} style={{flex: 0, flexWrap: "nowrap"}}>
                        <Grid item container size={7} alignItems={"center"}
                              style={{
                                  flexWrap: "nowrap",
                                  height: "40px",
                                  width: "100%",
                                  alignSelf: "flex-start",
                                  margin: "0 0 12px 0"
                              }}>
                            <IconButton
                                sx={iconButtonSx}
                                style={{height: "36px", width: "36px"}}
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (props.location.key === undefined)
                                        props.history.push("/productonboardingcenter/catalog?catalog=" + catalog);
                                    else
                                        props.history.goBack();
                                }}
                                size="large">
                                <ArrowBack style={{height: "18px", width: "18px"}}/>
                            </IconButton>
                            <Grid item container alignItems={"center"} size={11}
                                  style={{flex: 1, flexWrap: "nowrap", marginLeft: "12px"}}>
                                <Typography
                                    style={{
                                        fontSize: "24px",
                                        fontWeight: 600,
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        minWidth: "0",
                                    }}>
                                    {loading ? (
                                        <>Loading...</>
                                    ) : (
                                        <>
                                            {product.Attributes.product_page !== undefined ? (
                                                <Link target={"_blank"} href={product.Attributes.product_page}
                                                      sx={{
                                                          color: "black",
                                                          maxWidth: "clamp(0px, 100%, 100px)",
                                                          overflow: "hidden",
                                                          textOverflow: "ellipsis",
                                                          textDecoration: "none",
                                                          "&:hover": {
                                                              textDecoration: "underline"
                                                          }
                                                      }}
                                                >
                                                    {product.Attributes.name}
                                                </Link>
                                            ) : (
                                                <>
                                                    {!product.Attributes.name ? "New Product" : product.Attributes.name}
                                                </>
                                            )}
                                            {!props.isRetailer && (
                                                <Chip
                                                    // eslint-disable-next-line
                                                    label={supplierChannelData.find((channel) => channel.id == partnerId)?.name ?? urlPartnerId}
                                                    size={"small"}
                                                    style={{
                                                        margin: "0 25px",
                                                        height: "25px",
                                                        color: "#e0",
                                                        background: "transparent",
                                                        border: "1px solid #e0e0e0"
                                                    }}
                                                />
                                            )}
                                        </>
                                    )}
                                </Typography>
                                {product.Attributes.product_page !== undefined && (
                                    <Tooltip title={"Open product page"}>
                                        <IconButton
                                            target={"_blank"}
                                            href={product.Attributes.product_page}
                                            style={{margin: "2px"}}
                                            size="large">
                                            <OpenInNewIcon
                                                style={{color: (theme.palette as any).tertiary.main, fontSize: "24px"}}/>
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Grid>
                        </Grid>
                        <Grid item container size={5} justifyContent={"flex-end"} alignItems={"flex-start"}
                              style={{gap: "8px", height: "40px", margin: "0 0 12px 0"}}>
                            {hasInventoryManagePermission && (
                                <>
                                    {loading ? (<LoadingComponent height="40px"/>
                                    ) : (
                                        <Grid container justifyContent={"flex-end"}
                                              style={{width: "100%", maxHeight: "60px", overflow: "auto", gap: "6px"}}>
                                            {catalog === "mycatalog" && (
                                                <Tooltip
                                                    title={isDirty ? "Please save before duplicating" : ""}>
                                                    <div>
                                                        <Button variant="outlined"
                                                                style={{width: "84px"}}
                                                                disabled={updating || isDirty || productId === undefined}
                                                                onClick={(e) => {
                                                                    duplicateProduct();
                                                                }}>
                                                            Duplicate
                                                        </Button>
                                                    </div>
                                                </Tooltip>
                                            )}
                                            {isPartnerMarketplace && (
                                                <>
                                                    {(product.Attributes.sync_status === "Listed" || product.Attributes.sync_status === "List Pending") ? (
                                                        <Tooltip
                                                            title={isDirty ? "Please save before delisting" : ""}>
                                                            <div>
                                                                <Button variant="outlined"
                                                                        style={{width: "78px"}}
                                                                        disabled={updating || isDirty
                                                                            || (product.Attributes.sync_status === "Delisted")
                                                                            || (product.Attributes.sync_status === "Delist Pending")
                                                                            || productId === undefined
                                                                        }
                                                                        onClick={(e) => {
                                                                            delistProduct();
                                                                        }}>
                                                                    Delist
                                                                </Button>
                                                            </div>
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip
                                                            title={isDirty ? "Please save before listing" : ""}>
                                                            <div>
                                                                <Button variant="outlined"
                                                                        style={{width: "78px"}}
                                                                        disabled={updating || isDirty
                                                                            || (product.Attributes.sync_status === "Listed")
                                                                            || (product.Attributes.sync_status === "Listing Pending")
                                                                            || productId === undefined
                                                                        }
                                                                        onClick={(e) => {
                                                                            listProduct();
                                                                        }}>
                                                                    List
                                                                </Button>
                                                            </div>
                                                        </Tooltip>
                                                    )}
                                                </>
                                            )}
                                    {props.isRetailer && (
                                        <>
                                            {catalog === "mycatalog" ? (
                                                <></>
                                            ) : (
                                                <>
                                                    <Tooltip
                                                        title={isDirty ? "Please save before rejecting" : ""}>
                                                            <div>
                                                                <Button variant="outlined"
                                                                        style={{width: "78px"}}
                                                                        disabled={updating || isDirty
                                                                            || (product.Attributes.approval_status === "Not Compliant")
                                                                            || (product.Attributes.approval_status === "Approved")
                                                                            || productId === undefined
                                                                        }
                                                                        onClick={() => {
                                                                            setFinalizedProduct({
                                                                                sku: product.Attributes.sku,
                                                                                parentSku: product.Attributes.parent_sku,
                                                                                name: product.Attributes.name,
                                                                                image: product.Attributes.image,
                                                                                approvalStatus: product.Attributes.approval_status,
                                                                                supplierName: product.Attributes.supplier_name,
                                                                                supplierId: partnerId,
                                                                            });
                                                                            setRejectDialogOpen(true);
                                                                        }}>
                                                                    Reject
                                                                </Button>
                                                            </div>
                                                        </Tooltip>
                                                        <Tooltip
                                                            title={isDirty ? "Please save before approving" : ""}>
                                                                <div>
                                                                    <Button variant="outlined"
                                                                            style={{width: "78px"}}
                                                                            disabled={updating || isDirty
                                                                                || (product.Attributes.approval_status === "Approved")
                                                                                || productId === undefined
                                                                            }
                                                                            onClick={() => {
                                                                                setFinalizedProduct({
                                                                                    sku: product.Attributes.sku,
                                                                                    parentSku: product.Attributes.parent_sku,
                                                                                    name: product.Attributes.name,
                                                                                    image: product.Attributes.image,
                                                                                    approvalStatus: product.Attributes.approval_status,
                                                                                    supplierName: product.Attributes.supplier_name,
                                                                                    supplierId: partnerId,
                                                                                });
                                                                                setApproveDialogOpen(true);
                                                                            }}>
                                                                        Approve
                                                                    </Button>
                                                                </div>
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                </>
                                            )}
                                            <Button variant="contained" color="primary"
                                                data-testid="poc-product-save"
                                                style={{width: "78px"}}
                                                disabled={!isDirty || updating}
                                                onClick={(e) => {
                                                    if(variations.length > 0) {
                                                        setConfirmSaveDialogOpen(true);
                                                    } else {
                                                        if (productId === undefined) {
                                                            createProduct();
                                                        } else {
                                                            updateProduct(false);
                                                        }
                                                    }
                                                }}>
                                                Save
                                            </Button>
                                        </Grid>
                                    )}
                                </>
                            )}
                        </Grid>
                    </Grid>
                    <Grid container item size={12} style={{flex: 1}}>
                        {/*Attributes*/}
                        <Grid item size={8} style={{maxWidth: "50vw", overflow: "auto", paddingRight: "10px"}}>
                            {loading ? (
                                <LoadingComponent number={3} height="255px"/>
                            ) : (
                                <>
                                    {product.ErrorMessage !== undefined && product.ErrorMessage !== "" && (
                                        <Card id="cueMessage" style={{backgroundColor: '#ee3224', color: 'white', marginBottom: '24px'}}>
                                            <CardContent style={{display: 'flex', alignItems: 'center'}}>
                                                <ErrorIcon style={{marginRight: '1rem'}}/>
                                                <Typography style={{
                                                    textAlign: "left",
                                                    overflowY: "auto",
                                                    overflowX: "hidden",
                                                    maxHeight: "100px",
                                                    whiteSpace: "pre-line"
                                                }}>
                                                    {product.ErrorMessage}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    )}
                                    <Grid item container size={12} alignItems={"flex-start"}
                                          style={{width: "100%", gap: pageContentGap}}>
                                        <AttributesAndVariations
                                            groupedAttributes={groupedAttributes}
                                            formDisabled={formDisabled}
                                            catalog={catalog}
                                            productId={productId}
                                            product={product}
                                            retailerProduct={retailerProduct}
                                            availableCategories={availableCategories}
                                            validationErrors={validationErrors}
                                            updateProductState={updateProductState}
                                            setAddVariationDialog={setAddVariationDialog}
                                            onAIEnrich={handleAIEnrichment}
                                            setAddVariationSkuDialog={setAddVariationSkuDialog}
                                            variations={variations}
                                            setVariations={setVariations}
                                            allowVariations={allowVariations}
                                            variesBy={variesBy}
                                            availableAttributes={availableAttributes}
                                            setVariationDeletionDialogOpen={setVariationDeletionDialogOpen}
                                            setVariationDeletionIndex={setVariationDeletionIndex}
                                            hasInventoryManagePermission={hasInventoryManagePermission}
                                            updateVariationState={updateVariationState}
                                            partnerId={partnerId}
                                            isRetailer={props.isRetailer}
                                            variationValidationErrors={variationValidationErrors}
                                            refreshing={refreshing}
                                            aiApplyCounter={aiApplyCounter}
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>
                        {/*Static Attributes & Actions*/}
                        <Grid item size={4}>
                            {loading ? (
                                <LoadingComponent number={4} height="190px"/>
                            ) : (
                                <Grid item container size={12} alignItems={"flex-start"}
                                      style={{width: "100%", gap: pageContentGap}}>
                                        {AI_ASSIST_FEATURES() && (
                                            <ProductDetail
                                                hideTitle={true}
                                                content={
                                                    <ProductScoreCard
                                                        score={productScore}
                                                        suggestions={productSuggestions}
                                                        onScoreProduct={handleScoreProduct}
                                                        onViewDetails={handleViewDetails}
                                                        isLoading={false}
                                                        compact={productScore != null && (!productSuggestions || productSuggestions.length === 0)}
                                                    />
                                                }
                                            />
                                        )}
                                        <ProductDetail
                                            title={'Variations'}
                                            subtitle={
                                                (variesBy.options.length >= 25 || variesBy.fields.length >= 25) && (
                                                    <Alert severity={"info"} style={{}}>
                                                        <Typography variant={"body1"} align={"left"}>
                                                            Varies by options and fields are only displayed up to 25 fields each, should you
                                                            want to edit more fields please visit the product variant itself.
                                                        </Typography>
                                                    </Alert>
                                                )
                                            }
                                            content={
                                                <ProductVariations
                                                    variesBy={variesBy}
                                                    formDisabled={formDisabled}
                                                    catalog={catalog}
                                                    product={product}
                                                    initProduct={initProduct}
                                                    variations={variations}
                                                    setVariations={setVariations}
                                                    availableAttributes={availableAttributes}
                                                    hasInventoryManagePermission={hasInventoryManagePermission}
                                                    updateVariationState={updateVariationState}
                                                    variationValidationErrors={variationValidationErrors}
                                                    partnerId={partnerId}
                                                    setAddVariationDialog={setAddVariationDialog}
                                                    setAddVariationSkuDialog={setAddVariationSkuDialog}
                                                    setVariationDeletionDialogOpen={setVariationDeletionDialogOpen}
                                                    setVariationDeletionIndex={setVariationDeletionIndex}
                                                    allowVariations={allowVariations}
                                                />
                                            }
                                        />
                                    <ProductDetail
                                        paperStyles={{overflow: "auto"}}
                                        title={
                                            <AttributeDiff
                                                disabled={!retailerProduct}
                                                value={product.Attributes && product.Attributes["attribute_set"]}
                                                compare={retailerProduct && retailerProduct.Attributes && retailerProduct.Attributes["attribute_set"]}
                                                rejectChange={() => updateProductState("attribute_set", retailerProduct && retailerProduct.Attributes && retailerProduct.Attributes["attribute_set"])}
                                                label="Attribute Set *"
                                                labelstyles={{fontSize: "20px", fontWeight: 600, cursor: "pointer"}}
                                            />
                                        }
                                        content={
                                            <Grid item container size={12} data-testid="poc-product-attribute-set-select">
                                                <EnhancedSelect
                                                    isClearable
                                                    disabled={formDisabled}
                                                    error={validationErrors && validationErrors.attribute_set}
                                                    helperText={validationErrors && validationErrors.attribute_set}
                                                    value={product.Attributes && product.Attributes.attribute_set !== undefined && {
                                                        value: product.Attributes.attribute_set,
                                                        label: product.Attributes.attribute_set
                                                    }}
                                                    options={attributeSets.map((set) => {
                                                        return {value: set.Name, label: set.Name}
                                                    })}
                                                    onChange={(e) => {
                                                        e === null
                                                            ? updateProductState("attribute_set", "base")
                                                            : updateProductState("attribute_set", e.value === "" ? "base" : e.value);
                                                    }}
                                                />
                                            </Grid>
                                        }/>
                                    <ProductDetail
                                        title={
                                            <AttributeDiff
                                                disabled={!retailerProduct}
                                                value={product.Attributes && product.Attributes["tags"]}
                                                compare={retailerProduct && retailerProduct.Attributes && retailerProduct.Attributes["tags"]}
                                                rejectChange={() => updateProductState("tags", retailerProduct && retailerProduct.Attributes && retailerProduct.Attributes["tags"])}
                                                label="Tags"
                                                labelstyles={{fontSize: "20px", fontWeight: 600, cursor: "pointer"}}
                                            />
                                        }
                                        content={
                                            <EnhancedAutocomplete
                                                disabled={formDisabled}
                                                value={product.Attributes.tags === undefined ? [] : product.Attributes.tags}
                                                options={tags.filter((tag) => product.Attributes.tags != null ? !product.Attributes.tags.includes(tag) : [])}
                                                onChange={(e, value) => updateProductState("tags", value)}
                                            />
                                        }/>
                                    {/*// Display Listing Status as Retailer or if Supplier has sync_status //*/}
                                    {((props.isRetailer && catalog === "myCatalog") || (product.Attributes.sync_status && !props.isRetailer)) && (
                                        <ProductDetail title={"Listing Status"} content={
                                            <Grid item container style={{
                                                width: "100%",
                                                flexDirection: "column",
                                                alignItems: "flex-start",
                                                gap: pageContentGap
                                            }}>
                                                <EnhancedSelect
                                                    containerStyle={{width: "100%"}}
                                                    disabled={true}
                                                    value={{
                                                        value: product.Attributes.sync_status,
                                                        label: product.Attributes.sync_status
                                                    }}
                                                    options={[
                                                        {label: "Listing Failed", value: "Listing Failed"},
                                                        {label: "Not Listed", value: "Not Listed"},
                                                        {label: "Delist Pending", value: "Delist Pending"},
                                                        {label: "Listing Pending", value: "Listing Pending"},
                                                        {label: "Listed", value: "Listed"},
                                                        {label: "Delisted", value: "Delisted"},
                                                    ]}
                                                    onChange={(e) => updateProductState("sync_status", e.value)}/>
                                            </Grid>
                                        }/>
                                    )}
                                    {/*// Display Supplier Approval Status //*/}
                                    {(!isPartnerMarketplace && ((props.isRetailer && catalog === "partnercatalog")
                                        || (!props.isRetailer && catalog === "mycatalog"))) && (
                                        <ProductDetail title={"Approval Status"} content={
                                            <Grid item container style={{
                                                width: "100%",
                                                flexDirection: "column",
                                                alignItems: "flex-start",
                                                gap: pageContentGap
                                            }}>
                                                <EnhancedSelect
                                                    isClearable
                                                    disabled={formDisabled || !props.isRetailer}
                                                    containerStyle={{width: "100%"}}
                                                    value={{
                                                        value: product.Attributes.approval_status,
                                                        label: product.Attributes.approval_status
                                                    }}
                                                    options={[
                                                        {label: "Ignored", value: "Ignored"},
                                                        {label: "Flagged", value: "Flagged"},
                                                        {label: "Under Review", value: "Under Review"},
                                                        {label: "Update Suggested", value: "Update Suggested"},
                                                    ]}
                                                    onChange={(e) => updateProductState("approval_status", e ? e.value : '')}/>
                                            </Grid>
                                        }/>
                                    )}
                                    {availableAttributes.find(attribute => attribute.Name === "public_note") && (
                                        <ProductDetail title={"Public Notes"} content={
                                            <Grid item container style={{
                                                width: "100%",
                                                flexDirection: "column",
                                                alignItems: "flex-start",
                                                gap: pageContentGap
                                            }}>
                                                <EnhancedTextField
                                                    disabled={formDisabled}
                                                    style={{width: "100%"}}
                                                    inputstyles={{
                                                        maxHeight: "150px !important",
                                                        overflow: "auto !important"
                                                    }}
                                                    multiline
                                                    minRows={3}
                                                    value={product.Attributes.public_note}
                                                    onChange={(e) => updateProductState("public_note", e.target.value)}/>
                                            </Grid>
                                        }/>
                                    )}
                                    {availableAttributes.find(attribute => attribute.Name === "private_note") && (
                                        <ProductDetail title={"Internal Notes"} content={
                                            <Grid item container style={{
                                                width: "100%",
                                                flexDirection: "column",
                                                alignItems: "flex-start",
                                                gap: pageContentGap
                                            }}>
                                                <EnhancedTextField
                                                    disabled={formDisabled}
                                                    style={{width: "100%"}}
                                                    multilinestyles={{
                                                        maxHeight: "500px",
                                                        overflow: "auto"
                                                    }}
                                                    multiline
                                                    minRows={3}
                                                    value={product.Attributes.private_note}
                                                    onChange={(e) => updateProductState("private_note", e.target.value)}/>
                                            </Grid>
                                        }/>
                                    )}
                                    {(!isPartnerMarketplace && ((props.isRetailer && catalog === "partnercatalog")
                                        || (!props.isRetailer && catalog === "mycatalog"))) && (
                                        <ProductDetail title={"Collaboration Center"} content={
                                            <Grid container style={{flexDirection: "column"}}>
                                                <div style={{
                                                    flex: 1,
                                                    display: "flex",
                                                    justifyContent: "flex-start",
                                                    alignItems: "flex-start",
                                                    marginBottom: "6px"
                                                }}>
                                                    <Button variant="contained" color="primary"
                                                            onClick={() => {
                                                                setMessagerOpen(true);
                                                                setMessageMetadata((prevState) => ({
                                                                    ...prevState,
                                                                    postId: undefined,
                                                                    threadId: undefined,
                                                                }))
                                                            }}
                                                    >
                                                        <><Add style={{paddingRight: "6px"}}/> New Message</>
                                                    </Button>
                                                </div>
                                                <div style={{flex: 1, display: "block"}}>
                                                    {(messageMetadata.posts.length > 0) && (
                                                        <Timeline style={{fontSize: '100%'}}>
                                                            {messageMetadata.posts.map(post => {
                                                                return (
                                                                    <Post key={post.Id} coid={coid} post={post}
                                                                          sx={{
                                                                              "& img": {
                                                                                  maxWidth: "300px",
                                                                                  maxHeight: "300px"
                                                                              }
                                                                          }}
                                                                          reply={(postId, threadId) => {
                                                                              setMessagerOpen(true);
                                                                              setMessageMetadata((prevState) => ({
                                                                                  ...prevState,
                                                                                  postId: postId,
                                                                                  threadId: threadId
                                                                              }))
                                                                          }}
                                                                          contentStyle={{
                                                                              boxShadow: 'none',
                                                                              paddingLeft: 0
                                                                          }}/>
                                                                )
                                                            })}
                                                        </Timeline>
                                                    )}
                                                </div>
                                            </Grid>
                                        }/>
                                    )}
                                    <Grid container size={12} justifyContent={"flex-end"}>
                                        <Typography style={{color: "#7F7D83", textAlign: "right"}}>
                                            {product.Attributes.approved_by !== undefined ? "Approved By: " + product.Attributes.approved_by : ""}
                                            {product.Attributes.approval_date !== undefined
                                                ? " on " + moment(product.Attributes.approval_date).format("MM/DD/YYYY") + " at " + moment(product.Attributes.approval_date).format("h:mm A")
                                                : ""}
                                        </Typography>
                                    </Grid>
                                    <Grid container size={12} justifyContent={"flex-end"}>
                                        <Typography style={{color: "#7F7D83", textAlign: "right"}}>
                                            {product.Attributes.listed_by !== undefined ? "Listed By: " + product.Attributes.listed_by : ""}
                                            {product.Attributes.listed_date !== undefined
                                                ? " on " + moment(product.Attributes.listed_date).format("MM/DD/YYYY") + " at " + moment(product.Attributes.listed_date).format("h:mm A")
                                                : ""}
                                        </Typography>
                                    </Grid>
                                    <Grid container size={12} justifyContent={"flex-end"}>
                                        <Typography style={{color: "#7F7D83", textAlign: "right"}}>
                                            {product.Attributes.delisted_by !== undefined ? "Delisted By: " + product.Attributes.delisted_by : ""}
                                            {product.Attributes.delisted_date !== undefined
                                                ? " on " + moment(product.Attributes.delisted_date).format("MM/DD/YYYY") + " at " + moment(product.Attributes.delisted_date).format("h:mm A")
                                                : ""}
                                        </Typography>
                                    </Grid>
                                    <Grid container size={12} justifyContent={"flex-end"}>
                                        <Typography style={{color: "#7F7D83", textAlign: "right"}}>
                                            {product.LastModified !== undefined ? "Last Updated: " + moment(product.LastModified).format("h:mm A MM/DD/YYYY") : ""}
                                        </Typography>
                                    </Grid>
                                    <Grid container size={12} justifyContent={"flex-end"}>
                                        <Typography style={{color: "#7F7D83", textAlign: "right"}}>
                                            {product.Attributes.created_date !== undefined ? "Created On: " + moment(product.Attributes.created_date).format("h:mm A MM/DD/YYYY") : ""}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            {/*region Dialogs and Alerts*/}
            <NewMessage open={messagerOpen} onClose={() => setMessagerOpen(false)}
                        coid={coid} reference={productId} partner={partnerId}
                        posts={messageMetadata.posts}
                        postId={messageMetadata.postId}
                        threadId={messageMetadata.threadId}
                        setError={setError}
                        onSent={() => {
                            setMessagerOpen(false);
                            setMessageSentDialogOpen(true);
                            refreshPosts();
                        }}
            />
            <UpdatingDialog open={updating}/>
            <GenericDialog open={messageSentDialogOpen} title={"Message sent!"}
                           message={"Your message has been sent to your partner."}
                           setOpen={setMessageSentDialogOpen}
            />
            <GenericDialog open={duplicateDialogOpen} title={"Duplicate created"}
                           message={"A duplicate has been created, please enter a new SKU and save the product."}
                           setOpen={setDuplicateDialogOpen}
            />
            <EnhancedErrorDialog open={error.open} errorHeader={error.errorHeader} errorMessage={error.errorMessage}
                                 onClose={() => {
                                     setError({...error, open: false});
                                     if (loading) props.history.goBack();
                                 }}
            />
            <ProductApproveDialog
                isRetailer={props.isRetailer}
                forceUniqueRetailerSku={forceUniqueRetailerSku}
                setUpdating={setUpdating} setError={setError}
                open={approveDialogOpen} setOpen={setApproveDialogOpen}
                product={finalizedProduct as any}
                currentCatalog={catalog}
                onApprove={(success: boolean) => {
                    if (success) {
                        props.history.push("/productonboardingcenter/catalog?catalog=" + catalog);
                    }
                }}
            />
            <ProductRejectDialog
                isRetailer={props.isRetailer}
                setUpdating={setUpdating} setError={setError}
                open={rejectDialogOpen} setOpen={setRejectDialogOpen}
                product={finalizedProduct as any}
                currentCatalog={catalog}
                onReject={(((success: boolean) => {
                    if (success) {
                        props.history.push("/productonboardingcenter/catalog?catalog=" + catalog);
                    }
                }) as any)}
            />
            <EnhancedConfirmDialog open={variationDeletionDialogOpen} setOpen={setVariationDeletionDialogOpen}
                                   title={"Delete Variation?"} message={"This action cannot be undone."} type={"delete"}
                                   action={(action: boolean) => {
                                       if (action) {
                                           let updatedVariants = [...variations];
                                           let variantToDelete = cloneDeep(updatedVariants[variationDeletionIndex as any]);
                                           if (!updatedVariants[variationDeletionIndex as any].isNewVariation)
                                               deleteVariations([variantToDelete]);
                                           updatedVariants.splice(variationDeletionIndex as any, 1);
                                           setVariations(updatedVariants);
                                       }
                                   }}
            />
            <ProductVariantManager open={addVariationDialog} setOpen={setAddVariationDialog}
                                   availableAttributes={availableAttributes}
                                   variesBy={variesBy} setVariesBy={setVariesBy}
            />
            {confirmSaveDialogOpen && (
                <ProductSaveConfirm
                    open={confirmSaveDialogOpen}
                    setOpen={setConfirmSaveDialogOpen}
                    initProduct={initProduct}
                    product={product}
                    {...({initVariations, variations} as any)}
                    initVariesBy={initVariesBy as any}
                    variesBy={variesBy as any}
                    shouldUpdateVariations={variesBy.options && variesBy.options.length > 0}
                    saveOnlyProduct={() => {
                        setConfirmSaveDialogOpen(false);
                        if (productId === undefined) {
                            createProduct();
                        } else {
                            updateProduct(false);
                        }
                    }}
                    saveProductWithVariants={() => {
                        setConfirmSaveDialogOpen(false);
                        if (productId === undefined) {
                            createProduct();
                        } else {
                            updateProduct(true);
                        }
                    }}
                />
            )}
            {showSaveBeforeVariantDialog && (
                <ProductSaveConfirm
                    title={"Your product has changes, do you want to save them?"}
                    open={showSaveBeforeVariantDialog}
                    setOpen={setShowSaveBeforeVariantDialog}
                    initProduct={initProduct}
                    product={product}
                    {...({initVariations, variations} as any)}
                    initVariesBy={initVariesBy as any}
                    variesBy={variesBy as any}
                    shouldUpdateVariations={variesBy.options && variesBy.options.length > 0}
                    saveOnlyProduct={async () => {
                        setShowSaveBeforeVariantDialog(false);
                        // Save the product first
                        if (productId === undefined) {
                            await createProduct();
                        } else {
                            await updateProduct(false);
                        }
                        // Then create the variant and navigate
                        if (variantDataToCreate) {
                            await createVariantAndNavigate(variantDataToCreate);
                            setVariantDataToCreate(null);
                        }
                    }}
                    saveProductWithVariants={async () => {
                        setShowSaveBeforeVariantDialog(false);
                        // Save the product with variants
                        if (productId === undefined) {
                            await createProduct();
                        } else {
                            await updateProduct(true);
                        }
                        // Then create the variant and navigate
                        if (variantDataToCreate) {
                            await createVariantAndNavigate(variantDataToCreate);
                            setVariantDataToCreate(null);
                        }
                    }}
                />
            )}
            <ProductVariantAdd 
                open={addVariationSkuDialog} 
                setOpen={setAddVariationSkuDialog}
                onAdd={handleAddVariationSku}
                variesBy={variesBy}
                variations={variations}
                availableAttributes={availableAttributes}
                availableCategories={availableCategories}
            />
            <EnhancedSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            />
            {/* Product Score Dialog */}
            <ProductScoreDialog
                    open={scoreDialogOpen}
                    onClose={handleScoreDialogClose}
                    attributes={product.Attributes}
                    availableAttributes={availableAttributes}
                    onEnrichAttribute={handleScoreEnrichAttribute}
                    onScoreReceived={handleScoreReceived}
                    initialScore={productScore}
                    initialSuggestions={productSuggestions}
                    rescore={rescoreMode}
                    acceptedAttributes={acceptedEnrichments}
                    retailerCoid={retailerCoid}
                />
            {/* AI Enrichment Dialog — V2 from score dialog, V1 from inline icons */}
            {aiEnrichmentDialogOpen && (
                useV2Enrichment ? (
                    <AIEnrichmentDialogV2
                        open={aiEnrichmentDialogOpen}
                        onClose={() => {
                            setAIEnrichmentDialogOpen(false);
                            setAIEnrichmentAttribute(null);
                        }}
                        attributeName={aiEnrichmentAttribute}
                        currentAttributes={product.Attributes}
                        onApply={handleAIEnrichmentApply}
                        initialValue={product.Attributes[aiEnrichmentAttribute] || ''}
                        attributeMetadata={availableAttributes.find(attr => attr.Name === aiEnrichmentAttribute) || {}}
                    />
                ) : (
                    <AIEnrichmentDialog
                        open={aiEnrichmentDialogOpen}
                        onClose={() => {
                            setAIEnrichmentDialogOpen(false);
                            setAIEnrichmentAttribute(null);
                        }}
                        attributeName={aiEnrichmentAttribute}
                        currentAttributes={product.Attributes}
                        onApply={handleAIEnrichmentApply}
                        initialValue={product.Attributes[aiEnrichmentAttribute] || ''}
                        attributeMetadata={availableAttributes.find(attr => attr.Name === aiEnrichmentAttribute) || {}}
                    />
                )
            )}
            {/*endregion*/}
        </form>
    );
}
export default CatalogManagerProductDetails;
